import { action, makeObservable, observable } from 'mobx';
import { FormState } from 'formstate';

import { inject, injectable } from '@servicetitan/react-ioc';
import { commitFormState, InputFieldState } from '@servicetitan/form';

import { ExternalOptIn } from '../api/widget.api';
import {
    maxLengthValidation,
    phoneLengthValidation,
    requiredWithCustomText,
} from '../utils/helpers';
import { AuthStore } from './auth.store';
import { LoadStatus } from '../enums/load-status';
import { ErrorMessages, ErrorStatus, ExceptionStore } from './exception.store';

export const NAME_MAX_LENGTH = 128;

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type WidgetFormState = {
    name: InputFieldState<string>;
    phone: InputFieldState<string>;
};

@injectable()
export class WidgetStore {
    submitStatus = LoadStatus.None;

    formState: FormState<WidgetFormState> = new FormState({
        name: new InputFieldState('').validators(
            requiredWithCustomText('Name is required'),
            maxLengthValidation(NAME_MAX_LENGTH)
        ),
        phone: new InputFieldState('').validators(
            requiredWithCustomText('Phone number is required'),
            phoneLengthValidation('Invalid phone number')
        ),
    }).compose();

    constructor(
        @inject(ExternalOptIn)
        private api: ExternalOptIn,
        @inject(AuthStore)
        private authStore: AuthStore,
        @inject(ExceptionStore) private exceptionStore: ExceptionStore
    ) {
        makeObservable(this, {
            submitStatus: observable,
            setSubmitStatus: action,
        });
        this.formState.disableAutoValidation();
    }

    setSubmitStatus = (status: LoadStatus) => (this.submitStatus = status);

    handleSubmit = async () => {
        this.setSubmitStatus(LoadStatus.Loading);
        const { hasError } = await this.formState.enableAutoValidationAndValidate();
        if (hasError) {
            this.setSubmitStatus(LoadStatus.None);
            return;
        }

        try {
            if (this.authStore.widgetToken === undefined) {
                this.exceptionStore.stashError({
                    logger: true,
                    statusText: ErrorMessages.InstanceNotProvided,
                    statusCode: ErrorStatus.BADPAYLOAD,
                });
                this.setSubmitStatus(LoadStatus.Error);
                return;
            }

            await this.api.sendOptInRequest({
                formId: this.authStore.widgetToken,
                recipientName: this.formState.$.name.value,
                recipientPhoneNumber: this.formState.$.phone.value,
            });
            this.setSubmitStatus(LoadStatus.Ok);
            this.reset();
        } catch (e: any) {
            if (e.response?.status === ErrorStatus.VALIDATIONFAIL) {
                this.formState.$.phone.setError(e.response?.data.title);
            }
            this.setSubmitStatus(LoadStatus.Error);
        }
    };

    private reset = () => {
        this.formState.reset();
        commitFormState(this.formState);
    };
}
